/**
 * Trigger Configuration Section
 *
 * Allows configuration of automatic triggers for dynamic segments.
 * Used within the ListFormModal for segment creation/editing.
 */

import { useState, useEffect } from "react";
import type { UUID } from "../../types/common.types";
import type { ListTriggerType, TriggerConfig } from "../../types/list.types";
import {
  TRIGGER_TYPE_OPTIONS,
  EVALUATION_INTERVAL_OPTIONS,
} from "../../types/list.types";
import { getOffers } from "../../services/programs.service";
import type { ExtendedOffer } from "../../types/program.types";

interface TriggerConfigSectionProps {
  triggerType: ListTriggerType;
  triggerConfig: TriggerConfig;
  autoGenerateVouchers: boolean;
  linkedOfferId?: UUID | null;
  evaluationIntervalMinutes: number;
  isActive: boolean;
  onChange: (updates: {
    trigger_type?: ListTriggerType;
    trigger_config?: TriggerConfig;
    auto_generate_vouchers?: boolean;
    linked_offer_id?: UUID | null;
    evaluation_interval_minutes?: number;
    is_active?: boolean;
  }) => void;
  disabled?: boolean;
}

export default function TriggerConfigSection({
  triggerType,
  triggerConfig,
  autoGenerateVouchers,
  linkedOfferId,
  evaluationIntervalMinutes,
  isActive,
  onChange,
  disabled = false,
}: TriggerConfigSectionProps) {
  const [offers, setOffers] = useState<ExtendedOffer[]>([]);
  const [loadingOffers, setLoadingOffers] = useState(false);

  // Load offers when auto-generate is enabled
  useEffect(() => {
    if (autoGenerateVouchers && offers.length === 0) {
      loadOffers();
    }
  }, [autoGenerateVouchers]);

  const loadOffers = async () => {
    try {
      setLoadingOffers(true);
      const data = await getOffers();
      // Filter to active offers only
      setOffers(data.filter((o) => o.is_active && !o.is_deleted));
    } catch (error) {
      console.error("Error loading offers:", error);
    } finally {
      setLoadingOffers(false);
    }
  };

  const handleTriggerTypeChange = (type: ListTriggerType) => {
    // Reset config when trigger type changes
    const defaultConfig = getDefaultConfig(type);
    onChange({
      trigger_type: type,
      trigger_config: defaultConfig,
      // Reset auto-generate if trigger is NONE
      ...(type === "NONE" && {
        auto_generate_vouchers: false,
        linked_offer_id: null,
      }),
    });
  };

  const handleConfigChange = (key: string, value: unknown) => {
    onChange({
      trigger_config: {
        ...triggerConfig,
        [key]: value,
      },
    });
  };

  const getDefaultConfig = (type: ListTriggerType): TriggerConfig => {
    switch (type) {
      case "ANNIVERSARY":
        return { months: 12, date_field: "member_start_date" };
      case "RENEWAL_DUE":
        return { days_before: 30 };
      case "INACTIVITY":
        return { days: 90 };
      case "MILESTONE":
        return { field: "total_spend", threshold: 1000 };
      case "BIRTHDAY":
        return { days_before: 7 };
      case "CUSTOM":
        return { date_field: "created_at", days_offset: 0 };
      default:
        return {};
    }
  };

  const renderConfigFields = () => {
    switch (triggerType) {
      case "ANNIVERSARY":
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Months Since
              </label>
              <input
                type="number"
                min={1}
                value={(triggerConfig as { months?: number })?.months || 12}
                onChange={(e) =>
                  handleConfigChange("months", parseInt(e.target.value) || 12)
                }
                disabled={disabled}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                12 = 1 year, 24 = 2 years
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Date Field
              </label>
              <select
                value={
                  (triggerConfig as { date_field?: string })?.date_field ||
                  "member_start_date"
                }
                onChange={(e) =>
                  handleConfigChange("date_field", e.target.value)
                }
                disabled={disabled}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value="member_start_date">Member Start Date</option>
                <option value="member_date">Member Date</option>
                <option value="created_at">Created At</option>
              </select>
            </div>
          </div>
        );

      case "RENEWAL_DUE":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Days Before Expiration
            </label>
            <input
              type="number"
              min={1}
              value={
                (triggerConfig as { days_before?: number })?.days_before || 30
              }
              onChange={(e) =>
                handleConfigChange(
                  "days_before",
                  parseInt(e.target.value) || 30,
                )
              }
              disabled={disabled}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              Trigger X days before member_expiration_date
            </p>
          </div>
        );

      case "INACTIVITY":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Days of Inactivity
            </label>
            <input
              type="number"
              min={1}
              value={(triggerConfig as { days?: number })?.days || 90}
              onChange={(e) =>
                handleConfigChange("days", parseInt(e.target.value) || 90)
              }
              disabled={disabled}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              Trigger after X days of no activity
            </p>
          </div>
        );

      case "MILESTONE":
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Field to Check
              </label>
              <input
                type="text"
                value={
                  (triggerConfig as { field?: string })?.field || "total_spend"
                }
                onChange={(e) => handleConfigChange("field", e.target.value)}
                disabled={disabled}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Threshold Value
              </label>
              <input
                type="number"
                min={0}
                value={
                  (triggerConfig as { threshold?: number })?.threshold || 1000
                }
                onChange={(e) =>
                  handleConfigChange("threshold", parseInt(e.target.value) || 0)
                }
                disabled={disabled}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
          </div>
        );

      case "BIRTHDAY":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Days Before Birthday
            </label>
            <input
              type="number"
              min={0}
              value={
                (triggerConfig as { days_before?: number })?.days_before || 7
              }
              onChange={(e) =>
                handleConfigChange("days_before", parseInt(e.target.value) || 0)
              }
              disabled={disabled}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              0 = on birthday, 7 = one week before
            </p>
          </div>
        );

      case "CUSTOM":
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Date Field Name
              </label>
              <input
                type="text"
                value={
                  (triggerConfig as { date_field?: string })?.date_field || ""
                }
                onChange={(e) =>
                  handleConfigChange("date_field", e.target.value)
                }
                disabled={disabled}
                placeholder="e.g., custom_date"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Days Offset
              </label>
              <input
                type="number"
                value={
                  (triggerConfig as { days_offset?: number })?.days_offset || 0
                }
                onChange={(e) =>
                  handleConfigChange(
                    "days_offset",
                    parseInt(e.target.value) || 0,
                  )
                }
                disabled={disabled}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Negative = before date, positive = after
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4 border-t border-gray-200 pt-4 mt-4">
      <h3 className="text-lg font-medium text-gray-900">
        Trigger Configuration
      </h3>
      <p className="text-sm text-gray-500">
        Configure automatic triggers to issue vouchers when contacts match
        criteria
      </p>

      {/* Trigger Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Trigger Type
        </label>
        <select
          value={triggerType}
          onChange={(e) =>
            handleTriggerTypeChange(e.target.value as ListTriggerType)
          }
          disabled={disabled}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
        >
          {TRIGGER_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label} - {opt.description}
            </option>
          ))}
        </select>
      </div>

      {/* Trigger-specific Configuration */}
      {triggerType !== "NONE" && (
        <>
          <div className="bg-gray-50 p-4 rounded-md">
            {renderConfigFields()}
          </div>

          {/* Auto-generate Vouchers Toggle */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="auto-generate"
              checked={autoGenerateVouchers}
              onChange={(e) =>
                onChange({
                  auto_generate_vouchers: e.target.checked,
                  // Clear offer if disabling
                  ...(!e.target.checked && { linked_offer_id: null }),
                })
              }
              disabled={disabled}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label
              htmlFor="auto-generate"
              className="ml-2 block text-sm text-gray-900"
            >
              Automatically generate vouchers for matching contacts
            </label>
          </div>

          {/* Linked Offer Selection */}
          {autoGenerateVouchers && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Linked Offer
              </label>
              {loadingOffers ? (
                <p className="text-sm text-gray-500 mt-1">Loading offers...</p>
              ) : (
                <select
                  value={linkedOfferId || ""}
                  onChange={(e) =>
                    onChange({
                      linked_offer_id: e.target.value || null,
                    })
                  }
                  disabled={disabled}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">Select an offer...</option>
                  {offers.map((offer) => (
                    <option key={offer.id} value={offer.id}>
                      {offer.name} ({offer.offer_code})
                      {offer.value_amount ? ` - $${offer.value_amount}` : ""}
                    </option>
                  ))}
                </select>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Vouchers will be generated using this offer
              </p>
            </div>
          )}

          {/* Evaluation Interval */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Evaluation Frequency
            </label>
            <select
              value={evaluationIntervalMinutes}
              onChange={(e) =>
                onChange({
                  evaluation_interval_minutes: parseInt(e.target.value),
                })
              }
              disabled={disabled}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            >
              {EVALUATION_INTERVAL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              How often to check for new matching contacts
            </p>
          </div>

          {/* Active Toggle */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is-active"
              checked={isActive}
              onChange={(e) => onChange({ is_active: e.target.checked })}
              disabled={disabled}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label
              htmlFor="is-active"
              className="ml-2 block text-sm text-gray-900"
            >
              Trigger is active
            </label>
          </div>
        </>
      )}
    </div>
  );
}
